import { useCallback, useState, useEffect, useRef } from 'react'
import type { ChronoModule } from '../types'

export type ChronoLoadingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; chrono: ChronoModule }
  | { status: 'error'; error: string }

// Singleton cache for chrono-node module
let chronoCache: ChronoModule | null = null
let loadingPromise: Promise<ChronoModule> | null = null

async function loadChrono(): Promise<ChronoModule> {
  // Return cached module if already loaded
  if (chronoCache) {
    return chronoCache
  }

  // Return existing loading promise if already in progress
  if (loadingPromise) {
    return loadingPromise
  }

  // Start loading module
  loadingPromise = import('chrono-node')
    .then((chronoModule) => {
      chronoCache = chronoModule as unknown as ChronoModule
      loadingPromise = null // Reset for future loads if needed
      return chronoCache
    })
    .catch((err) => {
      loadingPromise = null // Reset promise on failure to allow retries
      throw err
    })

  return loadingPromise
}

type UseChronoLoaderResult = {
  loadingState: ChronoLoadingState
  loadModules: () => Promise<void>
}

/**
 * Custom hook to manage chrono-node loading with proper cleanup and cancellation.
 *
 * Features:
 * - Singleton pattern for module caching across component instances
 * - AbortController for proper async operation cancellation
 * - Automatic cleanup on component unmount
 * - Functional state updates to avoid unnecessary re-renders
 *
 * @returns Object containing loading state and function to load chrono-node module
 *
 * @example
 * ```tsx
 * const { loadingState, loadModules } = useChronoLoader()
 *
 * useEffect(() => {
 *   loadModules()
 * }, [loadModules])
 *
 * if (loadingState.status === 'ready') {
 *   const result = loadingState.chrono.parseDate('tomorrow at 3pm')
 * }
 * ```
 */
export const useChronoLoader = (): UseChronoLoaderResult => {
  const [loadingState, setLoadingState] = useState<ChronoLoadingState>({
    status: 'idle',
  })
  // Web standard for cancelling operations
  const abortControllerRef = useRef<AbortController | null>(null)

  const loadModules = useCallback(async (): Promise<void> => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current

    setLoadingState((prevState) =>
      prevState.status === 'idle' || prevState.status === 'error'
        ? { status: 'loading' }
        : prevState,
    )

    try {
      const chrono = await loadChrono()

      // Check if operation was cancelled
      if (!signal.aborted) {
        setLoadingState({ status: 'ready', chrono })
      }
    } catch (error) {
      if (!signal.aborted) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        // eslint-disable-next-line no-console
        console.error('Failed to load chrono-node module:', error)
        setLoadingState({ status: 'error', error: errorMessage })
      }
    }
  }, [])

  // Cleanup
  useEffect(() => {
    return (): void => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return { loadingState, loadModules }
}
